import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import Button from '../components/common/Button';
import Hero from '../components/common/Hero';
import Card from '../components/common/Card';
import FeatureBlock from '../components/common/FeatureBlock';
import Testimonial from '../components/common/Testimonial';

// Imagens temporárias (serão substituídas por imagens reais)
const heroImage = 'https://via.placeholder.com/600x400?text=Leadfy+Hero';
const featureImage1 = 'https://via.placeholder.com/500x300?text=Feature+1';
const featureImage2 = 'https://via.placeholder.com/500x300?text=Feature+2';
const featureImage3 = 'https://via.placeholder.com/500x300?text=Feature+3';

const HomePage = () => {
  const [stats, setStats] = useState([
    { value: '15x', label: 'Retorno sobre investimento' },
    { value: '55%', label: 'Redução no tempo de resposta' },
    { value: '65%', label: 'Taxa média de autoatendimento' }
  ]);

  return (
    <div>
      {/* Hero Section */}
      <Hero
        title="SISTEMA DE AUTOMAÇÃO CX MULTI-AGENTE"
        subtitle="Agentes de IA para Experiência do Cliente Empresarial"
        image={heroImage}
        actions={
          <>
            <Button size="large">Comece Hoje</Button>
            <Button variant="secondary" size="large">Saiba Mais</Button>
          </>
        }
      />

      {/* Why Leadfy Section */}
      <Section>
        <SectionTitle>Por que a Leadfy é a líder indiscutível em IA para CX</SectionTitle>
        
        <FeaturesGrid>
          <Card
            title="Totalmente Agêntico"
            hoverable
            icon={<i className="fas fa-robot"></i>}
          >
            Com Autoflows, nosso mecanismo de raciocínio de IA agêntico, entendemos a intenção do seu cliente e raciocinamos através de políticas de negócios complexas para tomar medidas e resolver seus problemas de ponta a ponta.
          </Card>
          
          <Card
            title="Treinado com Seus Dados"
            hoverable
            icon={<i className="fas fa-database"></i>}
          >
            Pronto desde o primeiro dia, o Leadfy é totalmente treinado em seus dados históricos para gerar Autoflows e criar artigos de central de ajuda com base em lacunas de conhecimento. É fácil de implementar e está sempre melhorando.
          </Card>
          
          <Card
            title="Sistema Multi-Agente"
            hoverable
            icon={<i className="fas fa-cogs"></i>}
          >
            Descubra o que precisa de atenção, Resolva problemas automaticamente, Classifique tickets de forma inteligente e Auxilie agentes humanos a ter o melhor desempenho — para que cada cliente veja o sucesso.
          </Card>
        </FeaturesGrid>
      </Section>

      {/* Results Section */}
      <Section background="light">
        <SectionTitle>Liderando o mercado com resultados incomparáveis</SectionTitle>
        <SectionDescription>
          A Leadfy entrega resultados que definem o padrão em IA para CX. Superamos e lideramos o mercado, garantindo que suas métricas de experiência do cliente sejam as melhores do setor. Não acredita? Vamos tentar uma prova de conceito.
        </SectionDescription>
        
        <StatsContainer>
          {stats.map((stat, index) => (
            <StatItem key={index}>
              <StatValue>{stat.value}</StatValue>
              <StatLabel>{stat.label}</StatLabel>
            </StatItem>
          ))}
        </StatsContainer>
        
        <CenteredButton>
          <Button size="large">Leia o Relatório de Benchmark 2025</Button>
        </CenteredButton>
      </Section>

      {/* Multi-Agent System Section */}
      <Section>
        <SectionTitle>Sistema Multi-Agente Leadfy</SectionTitle>
        <SectionSubtitle>Agentes de IA para suporte ao cliente mais inteligente</SectionSubtitle>
        <SectionDescription>
          Dê uma espiada nos bastidores com nossa demonstração interativa.
        </SectionDescription>
        
        <DemoContainer>
          <DemoPlaceholder>
            <p>Demonstração Interativa</p>
            <Button>Ver Demo</Button>
          </DemoPlaceholder>
        </DemoContainer>
      </Section>

      {/* Customer Success Stories */}
      <Section background="light">
        <SectionTitle>Leia histórias de sucesso de clientes</SectionTitle>
        
        <TestimonialContainer>
          <Testimonial
            quote="Estávamos trabalhando com uma empresa diferente antes. Eles não se apresentam como um produto de IA porque funcionam de forma diferente – eu tinha que treinar cada fluxo de trabalho eu mesmo. Tínhamos milhares de fluxos de trabalho construídos que eram frequentemente duplicados e respondiam às perguntas incorretamente. Tornou-se um grande monstro que era muito complicado de gerenciar."
            name="Alix Perez"
            role="Gerente de Projeto para Produtos de Chatbot e Central de Ajuda"
            stats={[
              { value: '90%', label: 'Precisão e cobertura na classificação de novos tickets' },
              { value: '50%', label: 'Redução no tempo de resolução' },
              { value: '65%', label: 'Taxa média de autoatendimento' }
            ]}
          />
        </TestimonialContainer>
      </Section>

      {/* Solutions Section */}
      <Section>
        <SectionTitle>Soluções por Função e Indústria</SectionTitle>
        <SectionSubtitle>IA empresarial escalável para equipes de suporte</SectionSubtitle>
        
        <FeatureBlock
          title="IA empresarial escalável para equipes de suporte"
          description="A Leadfy ajuda equipes de suporte empresarial a entregar resoluções mais rápidas, capacitar agentes e criar experiências excepcionais para o cliente — seja você operando globalmente ou regionalmente."
          image={featureImage1}
          imagePosition="right"
          actions={<Button>Ver Todas as Soluções</Button>}
        />
      </Section>

      {/* Trusted Brands Section */}
      <Section background="light">
        <SectionTitle>Confiado por marcas que entregam serviço excepcional</SectionTitle>
        
        <BrandsContainer>
          {/* Placeholder para logos de marcas */}
          <BrandLogo>Logo 1</BrandLogo>
          <BrandLogo>Logo 2</BrandLogo>
          <BrandLogo>Logo 3</BrandLogo>
          <BrandLogo>Logo 4</BrandLogo>
          <BrandLogo>Logo 5</BrandLogo>
        </BrandsContainer>
        
        <Quotes>
          <Quote>
            "O que fez a Leadfy ressoar com os clientes é um software que se conecta ao software de vendas e atendimento ao cliente de uma empresa para aprender como problemas passados foram resolvidos e prever melhor do que se trata um ticket individual."
          </Quote>
          
          <Quote>
            "Quando um ticket está chegando, ele pode prever se é um ticket de alta ou baixa prioridade e qual agente está melhor qualificado para lidar com esta questão. E tudo isso acontece antes mesmo do agente tocar no ticket."
          </Quote>
        </Quotes>
      </Section>

      {/* CTA Section */}
      <Section>
        <SectionTitle>Suporte mais rápido, mais inteligente e mais humano</SectionTitle>
        <SectionDescription>
          Libere sua equipe para se concentrar em conversas de alto valor com clientes que constroem relacionamentos duradouros.
        </SectionDescription>
        
        <CenteredButton>
          <Button size="large">Solicite uma Demo</Button>
        </CenteredButton>
      </Section>
    </div>
  );
};

// Styled Components
const Section = styled.section`
  padding: var(--spacing-xxl) 0;
  background-color: ${props => props.background === 'light' ? 'var(--light-bg)' : 'var(--white)'};
`;

const SectionTitle = styled.h2`
  font-size: var(--font-size-4xl);
  text-align: center;
  margin-bottom: var(--spacing-lg);
  color: var(--primary-dark);
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
`;

const SectionSubtitle = styled.h3`
  font-size: var(--font-size-2xl);
  text-align: center;
  margin-bottom: var(--spacing-xl);
  color: var(--text-secondary);
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
`;

const SectionDescription = styled.p`
  font-size: var(--font-size-lg);
  text-align: center;
  margin-bottom: var(--spacing-xl);
  color: var(--text-secondary);
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--spacing-lg);
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--spacing-lg);
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const StatsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: var(--spacing-xxl);
  margin: var(--spacing-xl) 0;
  flex-wrap: wrap;
`;

const StatItem = styled.div`
  text-align: center;
  min-width: 200px;
`;

const StatValue = styled.div`
  font-size: var(--font-size-5xl);
  font-weight: 700;
  color: var(--primary);
  margin-bottom: var(--spacing-sm);
`;

const StatLabel = styled.div`
  font-size: var(--font-size-md);
  color: var(--text-secondary);
`;

const CenteredButton = styled.div`
  display: flex;
  justify-content: center;
  margin-top: var(--spacing-xl);
`;

const DemoContainer = styled.div`
  max-width: 900px;
  margin: var(--spacing-xl) auto;
  height: 500px;
  border-radius: var(--border-radius-lg);
  overflow: hidden;
`;

const DemoPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  background-color: var(--light-bg);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
  font-size: var(--font-size-xl);
  
  p {
    margin-bottom: var(--spacing-lg);
  }
`;

const TestimonialContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 var(--spacing-lg);
`;

const BrandsContainer = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: var(--spacing-xl);
  margin: var(--spacing-xl) 0;
`;

const BrandLogo = styled.div`
  width: 180px;
  height: 100px;
  background-color: var(--white);
  border-radius: var(--border-radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: var(--shadow-sm);
`;

const Quotes = styled.div`
  max-width: 900px;
  margin: var(--spacing-xl) auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--spacing-xl);
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Quote = styled.blockquote`
  font-size: var(--font-size-lg);
  color: var(--text-secondary);
  line-height: 1.6;
  position: relative;
  padding: var(--spacing-lg);
  
  &::before {
    content: '"';
    position: absolute;
    top: 0;
    left: 0;
    font-size: 3rem;
    color: var(--primary-light);
    opacity: 0.3;
  }
`;

export default HomePage;
